import React, { useContext } from 'react';
import classNames from 'classnames';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { BasketResponse } from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { LoadingBar } from '../LoadingBar';
import styles from './OrderSummary.module.scss';

interface OrderSummaryProps {
	className?: string;
	basket: BasketResponse | null;
	isSkeleton?: boolean;
}

/**
 * OrderSummary component, used for displaying price details in order overview
 */
export const OrderSummary: React.FunctionComponent<OrderSummaryProps> = ({ className, basket, isSkeleton }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings, isLoading } = useGetSettings(segmentationId);

	if (isLoading) return null;

	const currencyName = settings?.data.currencySettings.currencyName;
	const cultureCode = settings?.data.cultureCode;

	const numberFormatter =
		cultureCode && currencyName
			? new Intl.NumberFormat(cultureCode, { style: 'currency', currency: currencyName })
			: new Intl.NumberFormat();

	return (
		<div className={className}>
			<p className={classNames(styles.totalLine, styles.isBold)}>
				<span>{translations?.data.orderSummary.subtotal}</span>
				{isSkeleton ? (
					<LoadingBar height="1em" width="6ch" />
				) : (
					<span>{basket?.totalInclVat != null && numberFormatter.format(basket.totalInclVat)}</span>
				)}
			</p>
			<p className={styles.totalLine}>
				<span>{translations?.data.orderSummary.vat}</span>
				{isSkeleton ? (
					<LoadingBar height="1em" width="6ch" />
				) : (
					<span>{basket?.totalVat != null && numberFormatter.format(basket.totalVat)}</span>
				)}
			</p>
			<hr className={'u-mb-md'} />
			<p className={classNames(styles.totalLine, styles.isBold, styles.isLarge, 'u-mt-md')}>
				<span>{translations?.data.orderSummary.totalPrice}</span>
				{isSkeleton ? (
					<LoadingBar height="1em" width="6ch" />
				) : (
					<span>{basket?.totalInclVat != null && numberFormatter.format(basket.totalInclVat)}</span>
				)}
			</p>
		</div>
	);
};
